import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Pencil,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  parseCSV,
  ParseResult,
  ColumnMapping,
  transformRow,
  ValidationResult,
  getAvailableFields,
  generateTemplateCSV,
  getTemplateData,
} from "@/lib/csv-parser";
import * as XLSX from "xlsx";

interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onImportComplete: () => void;
}

type ImportStep = "upload" | "mapping" | "preview" | "importing" | "complete";

interface SkippedItem {
  row: number;
  name: string;
  reason: string;
  message: string;
}

interface ImportResult {
  success: number;
  alreadyExists: SkippedItem[];
  csvDuplicates: SkippedItem[];
  errors: SkippedItem[];
}

const SKIP_REASON_LABELS: Record<string, string> = {
  name_already_exists: "Produto com este nome já existe no catálogo",
  exists_no_update: "Produto já existe (marque 'Atualizar existentes' para sobrescrever)",
  sku_reference_conflict: "SKU e Referência apontam para produtos diferentes",
  update_name_conflict: "Nome em uso por outro produto do catálogo",
  csv_duplicate_sku: "SKU duplicado dentro do próprio arquivo",
  csv_duplicate_reference: "Referência duplicada dentro do próprio arquivo",
  csv_duplicate_name: "Nome duplicado dentro do próprio arquivo",
  insert_error: "Erro ao inserir no banco de dados",
  update_error: "Erro ao atualizar no banco de dados",
};

const CSV_DUP_REASONS = new Set([
  "csv_duplicate_sku",
  "csv_duplicate_reference",
  "csv_duplicate_name",
]);

const ALREADY_EXISTS_REASONS = new Set([
  "name_already_exists",
  "exists_no_update",
  "sku_reference_conflict",
  "update_name_conflict",
]);

function humanizeSkipMessage(reason: string, fallback?: string): string {
  if (SKIP_REASON_LABELS[reason]) return SKIP_REASON_LABELS[reason];
  if (fallback) {
    if (fallback.includes("unique_product_per_workspace") || fallback.includes("duplicate key")) {
      return "Produto com este nome já existe no catálogo";
    }
    return fallback;
  }
  return "Item ignorado";
}

export function CsvImportDialog({
  open,
  onOpenChange,
  workspaceId,
  onImportComplete,
}: CsvImportDialogProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<ImportStep>("upload");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [validatedRows, setValidatedRows] = useState<ValidationResult[]>([]);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "valid" | "error">("all");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [templateType, setTemplateType] = useState<"product" | "service" | "plan">("product");
  const [templateFormat, setTemplateFormat] = useState<"csv" | "xlsx">("csv");

  const availableFields = getAvailableFields();

  const resetDialog = useCallback(() => {
    setStep("upload");
    setParseResult(null);
    setMappings([]);
    setValidatedRows([]);
    setUpdateExisting(false);
    setImporting(false);
    setImportProgress(0);
    setImportResult(null);
    setFilterStatus("all");
    setEditingIndex(null);
    setEditValues({});
  }, []);

  const handleClose = useCallback(
    (shouldRefresh: boolean = false) => {
      if (importing) return;
      resetDialog();
      onOpenChange(false);
      if (shouldRefresh) {
        onImportComplete();
      }
    },
    [importing, onOpenChange, onImportComplete, resetDialog]
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const isValidType =
      validTypes.includes(file.type) ||
      file.name.endsWith(".csv") ||
      file.name.endsWith(".xlsx");

    if (!isValidType) {
      toast({
        title: "Formato inválido",
        description: "Por favor, selecione um arquivo CSV ou Excel.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await parseCSV(file);

      if (result.rows.length === 0) {
        toast({
          title: "Arquivo vazio",
          description: "O arquivo não contém dados para importar.",
          variant: "destructive",
        });
        return;
      }

      if (result.rows.length > 4000) {
        toast({
          title: "Arquivo muito grande",
          description: "O limite é de 4000 itens por importação.",
          variant: "destructive",
        });
        return;
      }

      setParseResult(result);
      setMappings(result.mappings);
      setStep("mapping");
    } catch (error) {
      toast({
        title: "Erro ao processar arquivo",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  const handleMappingChange = (csvColumn: string, dbField: string | null) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.csvColumn === csvColumn
          ? { ...m, dbField: dbField === "none" ? null : dbField, autoDetected: false }
          : m
      )
    );
  };

  const saveMappingsToLocalStorage = () => {
    if (!parseResult) return;
    const schemaSignature = parseResult.headers.join("|");
    const mappingsToSave: Record<string, string | null> = {};
    mappings.forEach(m => {
      mappingsToSave[m.csvColumn] = m.dbField;
    });
    localStorage.setItem(`csv_mapping_${schemaSignature}`, JSON.stringify(mappingsToSave));
  };

  const handleContinueToPreview = () => {
    if (!parseResult) return;

    // Verificar se nome está mapeado
    const hasNameMapping = mappings.some((m) => m.dbField === "name");
    if (!hasNameMapping) {
      toast({
        title: "Campo obrigatório",
        description: "É necessário mapear pelo menos a coluna 'Nome do produto'.",
        variant: "destructive",
      });
      return;
    }

    // Verificar conflitos
    const mappedFields = mappings.filter(m => m.dbField !== null);
    const fieldCounts = mappedFields.reduce((acc, m) => {
      acc[m.dbField!] = (acc[m.dbField!] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const conflicts = Object.entries(fieldCounts).filter(([field, count]) => count > 1);
    if (conflicts.length > 0) {
      const conflictLabels = conflicts.map(([field]) => availableFields.find(f => f.value === field)?.label || field);
      toast({
        title: "Conflito de mapeamento",
        description: `Os seguintes campos foram mapeados mais de uma vez: ${conflictLabels.join(", ")}. Por favor, corrija os conflitos destacados em vermelho.`,
        variant: "destructive",
      });
      return;
    }

    // Salvar escolha do usuário para a próxima vez
    saveMappingsToLocalStorage();

    // Transformar e validar todas as linhas
    const validated = parseResult.rows.map((row, idx) => ({
      ...transformRow(row, mappings),
      originalIndex: idx
    }));
    setValidatedRows(validated as any);
    setStep("preview");
  };

  const validCount = validatedRows.filter((r) => r.isValid).length;
  const errorCount = validatedRows.filter((r) => !r.isValid).length;

  const handleStartImport = async () => {
    if (!workspaceId) return;

    setImporting(true);
    setStep("importing");
    setImportProgress(0);

    const validItems = validatedRows.filter((r) => r.isValid);
    const result: ImportResult = {
      success: 0,
      alreadyExists: [],
      csvDuplicates: [],
      errors: [],
    };

    try {
      setImportProgress(10);

      const productsPayload = validItems.map((item: any) => ({
        original_row: item.originalIndex + 1,
        name: item.data.name,
        type: item.data.type,
        is_active: item.data.is_active,
        description: item.data.description || null,
        price: item.data.price ?? null,
        sku: item.data.sku || null,
        stock: item.data.stock ?? null,
        colors: item.data.colors?.length ? item.data.colors : null,
        dimensions: item.data.dimensions || null,
        duration: item.data.duration ?? null,
        service_category: item.data.service_category || null,
        plan_billing_type: item.data.plan_billing_type || null,
        plan_features: item.data.plan_features?.length ? item.data.plan_features : null,
        plan_trial_days: item.data.plan_trial_days ?? null,
      }));

      setImportProgress(30);

      const { data: importResp, error: importError } = await supabase.functions.invoke(
        "import-products",
        {
          body: {
            workspaceId,
            products: productsPayload,
            updateExisting,
          },
        }
      );

      if (importError) throw importError;
      if (importResp?.error) throw new Error(importResp.error);

      setImportProgress(100);

      result.success = (importResp?.inserted || 0) + (importResp?.updated || 0);

      if (Array.isArray(importResp?.skipped)) {
        for (const skip of importResp.skipped) {
          const reason = skip.reason || "unknown";
          const item: SkippedItem = {
            row: skip.row || 0,
            name: skip.name || "Item",
            reason,
            message: humanizeSkipMessage(reason, skip.message),
          };

          if (CSV_DUP_REASONS.has(reason)) {
            result.csvDuplicates.push(item);
          } else if (ALREADY_EXISTS_REASONS.has(reason)) {
            result.alreadyExists.push(item);
          } else {
            result.errors.push(item);
          }
        }
      }
    } catch (error: any) {
      result.errors.push({
        row: 0,
        name: "Importação",
        reason: "invoke_error",
        message: error?.message || "Erro ao chamar serviço de importação",
      });
    }

    setImportResult(result);
    setImporting(false);
    setStep("complete");
  };

  const handleDownloadTemplate = () => {
    if (templateFormat === "csv") {
      const csv = generateTemplateCSV(templateType);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `template-${templateType}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      const rows = getTemplateData(templateType);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, `template-${templateType}.xlsx`);
    }
  };

  const handleEditClick = (originalIndex: number) => {
    setEditingIndex(originalIndex);
    if (parseResult) {
      setEditValues({ ...parseResult.rows[originalIndex] });
    }
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !parseResult) return;
    
    // Atualizar os dados brutos no parseResult
    const newRows = [...parseResult.rows];
    newRows[editingIndex] = editValues;
    setParseResult({ ...parseResult, rows: newRows });
    
    // Revalidar apenas essa linha e atualizar
    const newValidated = [...validatedRows];
    newValidated[editingIndex] = { ...transformRow(editValues, mappings), originalIndex: editingIndex };
    setValidatedRows(newValidated as any);
    
    setEditingIndex(null);
  };

  const handleRetryErrors = () => {
    if (!importResult || !parseResult) return;
    
    const failedRowsList = [...importResult.errors, ...importResult.csvDuplicates];
    const failedOriginalIndices = new Set(failedRowsList.map(item => item.row - 1));
    const serverErrorMap = new Map(failedRowsList.map(item => [item.row - 1, item.message]));
    
    const clientSideFailedIndices = new Set(
      validatedRows
        .filter((r: any) => !r.isValid)
        .map((r: any) => r.originalIndex)
    );

    const allFailedIndices = new Set([...failedOriginalIndices, ...clientSideFailedIndices]);

    if (allFailedIndices.size === 0) return;

    const newParseRows: any[] = [];
    const newValidated: any[] = [];
    
    let newIdx = 0;
    parseResult.rows.forEach((row, oldIdx) => {
      if (allFailedIndices.has(oldIdx)) {
        newParseRows.push(row);
        
        const res = transformRow(row, mappings);
        if (serverErrorMap.has(oldIdx)) {
          res.isValid = false;
          res.errors.push(serverErrorMap.get(oldIdx)!);
        }
        
        newValidated.push({ ...res, originalIndex: newIdx });
        newIdx++;
      }
    });

    setParseResult({ ...parseResult, rows: newParseRows });
    setValidatedRows(newValidated as any);
    setFilterStatus("error");
    setStep("preview");
  };

  const renderUploadStep = () => (
    <div className="space-y-6 py-4">
      <div className="border-2 border-dashed rounded-lg p-8 text-center">
        <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-4">
          Arraste um arquivo CSV ou Excel aqui, ou clique para selecionar
        </p>
        <Input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="max-w-xs mx-auto"
        />
        <p className="text-xs text-muted-foreground mt-3">
          Formatos aceitos: CSV, XLSX • Máximo: 4000 itens
        </p>
      </div>

      <div className="flex items-center justify-center gap-4">
        <div className="h-px bg-border flex-1" />
        <span className="text-xs text-muted-foreground">ou</span>
        <div className="h-px bg-border flex-1" />
      </div>

      <div className="bg-muted/30 p-4 rounded-lg space-y-4 border border-border">
        <div className="flex items-center gap-2 mb-2">
          <Download className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">Baixar Template</h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Tipo de item</Label>
            <Select value={templateType} onValueChange={(val: any) => setTemplateType(val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="product">Produtos</SelectItem>
                <SelectItem value="service">Serviços</SelectItem>
                <SelectItem value="plan">Planos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Formato</Label>
            <Select value={templateFormat} onValueChange={(val: any) => setTemplateFormat(val)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full"
          onClick={handleDownloadTemplate}
        >
          <Download className="w-4 h-4 mr-2" />
          Baixar arquivo
        </Button>
      </div>
    </div>
  );

  const renderMappingStep = () => {
    // Calcular contagens para feedback visual de conflitos em tempo real
    const mappedFields = mappings.filter(m => m.dbField !== null);
    const fieldCounts = mappedFields.reduce((acc, m) => {
      acc[m.dbField!] = (acc[m.dbField!] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const hasNameMapping = mappings.some((m) => m.dbField === "name");
    const hasConflicts = Object.entries(fieldCounts).some(([_, count]) => count > 1);

    return (
      <div className="space-y-4 py-4">
        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">
            Revise o mapeamento das colunas. O sistema sugeriu campos com base no histórico e nos nomes das colunas. Você deve confirmar ou alterar o destino.
          </p>
          
          {hasConflicts && (
            <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span><strong>Conflito:</strong> Existem colunas mapeadas para o mesmo campo de destino. Corrija os erros em vermelho.</span>
            </div>
          )}
          
          {!hasNameMapping && (
            <div className="p-3 bg-yellow-500/10 text-yellow-700 text-sm rounded-md flex items-center gap-2 border border-yellow-500/20">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span><strong>Atenção:</strong> O campo obrigatório "Nome do produto" não está mapeado.</span>
            </div>
          )}
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-3 pr-4">
            {mappings.map((mapping) => {
               const isConflict = mapping.dbField && fieldCounts[mapping.dbField] > 1;
               return (
                <div
                  key={mapping.csvColumn}
                  className={`p-3 border rounded-lg overflow-hidden ${isConflict ? 'border-destructive bg-destructive/5' : 'bg-card'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold text-sm break-all" title={mapping.csvColumn}>
                          {mapping.csvColumn}
                        </p>
                        {mapping.confidence === "high" && <Badge className="bg-green-500 hover:bg-green-600 text-[10px] px-1.5 py-0 h-4 flex-shrink-0">Alta Confiança</Badge>}
                        {mapping.confidence === "medium" && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-yellow-500/20 text-yellow-700 hover:bg-yellow-500/30 flex-shrink-0">Confiança Média</Badge>}
                        {mapping.confidence === "low" && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">Baixa Confiança</Badge>}
                      </div>
                      
                      {mapping.samples && mapping.samples.length > 0 ? (
                        <div className="mt-1.5">
                          <p className="text-[11px] text-muted-foreground font-medium mb-0.5 uppercase tracking-wider">Amostras da coluna:</p>
                          <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                            {mapping.samples.map((sample, idx) => (
                              <li key={idx} className="break-all" title={sample}>{sample}</li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic mt-1.5">Nenhum valor encontrado nesta coluna</p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1.5 w-[200px] flex-shrink-0">
                      <Select
                        value={mapping.dbField || "none"}
                        onValueChange={(value) =>
                          handleMappingChange(mapping.csvColumn, value)
                        }
                      >
                        <SelectTrigger className={`w-full ${isConflict ? 'border-destructive ring-destructive' : ''} ${!mapping.dbField ? 'text-muted-foreground italic' : 'font-medium'}`}>
                          <SelectValue placeholder="Ignorar coluna" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" className="text-muted-foreground italic font-normal">Ignorar coluna</SelectItem>
                          {availableFields.map((field) => (
                            <SelectItem key={field.value} value={field.value}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isConflict && (
                        <span className="text-[10px] text-destructive font-medium text-right">
                          Este campo de destino já foi escolhido!
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  };

  const renderPreviewStep = () => {
    const mappedFields = mappings.filter(m => m.dbField !== null);
    
    const filteredRows = validatedRows.filter((row) => {
      if (filterStatus === "valid") return row.isValid;
      if (filterStatus === "error") return !row.isValid;
      return true;
    });

    return (
    <div className="space-y-4 py-4">
      <div className="bg-muted/50 p-4 rounded-lg border">
        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          Resumo do Mapeamento
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
          {mappedFields.map(m => {
            const fieldLabel = availableFields.find(f => f.value === m.dbField)?.label || m.dbField;
            return (
              <div key={m.csvColumn} className="flex items-center gap-2 text-sm overflow-hidden">
                <span className="font-semibold whitespace-nowrap">{fieldLabel}</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground truncate" title={m.csvColumn}>{m.csvColumn}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button 
            type="button"
            onClick={() => setFilterStatus(filterStatus === "valid" ? "all" : "valid")}
            className="focus:outline-none transition-opacity hover:opacity-80"
          >
            <Badge 
              variant={filterStatus === "all" || filterStatus === "valid" ? "default" : "outline"} 
              className={`gap-1 cursor-pointer ${filterStatus === "error" ? "text-green-600 border-green-500/50" : "bg-green-500 hover:bg-green-600"}`}
            >
              <CheckCircle className="w-3 h-3" />
              {validCount} válidos
            </Badge>
          </button>
          
          {errorCount > 0 && (
            <button 
              type="button"
              onClick={() => setFilterStatus(filterStatus === "error" ? "all" : "error")}
              className="focus:outline-none transition-opacity hover:opacity-80"
            >
              <Badge 
                variant={filterStatus === "all" || filterStatus === "error" ? "destructive" : "outline"} 
                className={`gap-1 cursor-pointer ${filterStatus === "valid" ? "text-destructive border-destructive/50" : ""}`}
              >
                <XCircle className="w-3 h-3" />
                {errorCount} com erro
              </Badge>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="update-existing"
            checked={updateExisting}
            onCheckedChange={(checked) => setUpdateExisting(checked === true)}
          />
          <Label htmlFor="update-existing" className="text-sm">
            Atualizar existentes por SKU
          </Label>
        </div>
      </div>

      <ScrollArea className="h-[320px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">Status</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Preço</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.slice(0, 50).map((row) => (
              editingIndex === row.originalIndex ? (
                <TableRow key={row.originalIndex}>
                  <TableCell colSpan={6} className="p-0 border-b">
                    <div className="bg-muted/30 p-4 border-l-2 border-l-primary space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Pencil className="w-4 h-4 text-primary" />
                          Corrigir item (Linha {row.originalIndex + 1})
                        </h4>
                        {row.errors.length > 0 && Object.keys(row.fieldErrors || {}).length === 0 && (
                          <span className="text-xs text-destructive">{row.errors.join(", ")}</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {mappings.filter(m => m.dbField).map(m => {
                          const hasError = row.fieldErrors && row.fieldErrors[m.dbField!];
                          return (
                            <div key={m.csvColumn} className="space-y-1.5">
                              <Label className="text-xs flex items-center justify-between">
                                {availableFields.find(f => f.value === m.dbField)?.label}
                                <span className="text-[10px] text-muted-foreground font-normal truncate max-w-[100px]" title={m.csvColumn}>
                                  ({m.csvColumn})
                                </span>
                              </Label>
                              <Input 
                                value={editValues[m.csvColumn] || ""} 
                                onChange={e => setEditValues({...editValues, [m.csvColumn]: e.target.value})}
                                className={`h-8 text-sm ${hasError ? 'border-destructive focus-visible:ring-destructive' : 'bg-background'}`}
                                placeholder="Em branco"
                              />
                              {hasError && (
                                <span className="text-[10px] text-destructive block">{row.fieldErrors[m.dbField!]}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingIndex(null)}>
                          Cancelar
                        </Button>
                        <Button size="sm" onClick={handleSaveEdit}>
                          Salvar Correção
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
              <TableRow key={row.originalIndex} className={!row.isValid ? "bg-destructive/5" : ""}>
                <TableCell>
                  {row.isValid ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-destructive" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {row.data.name || (
                    <span className="text-destructive">[vazio]</span>
                  )}
                  {row.fieldErrors && Object.keys(row.fieldErrors).length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(row.fieldErrors).map(([field, msg]) => (
                        <p key={field} className="text-xs text-destructive flex items-start gap-1">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {msg}
                        </p>
                      ))}
                    </div>
                  )}
                  {row.errors.length > 0 && Object.keys(row.fieldErrors || {}).length === 0 && (
                    <p className="text-xs text-destructive mt-1">
                      {row.errors.join(", ")}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {row.data.type === "product"
                      ? "Produto"
                      : row.data.type === "plan"
                        ? "Plano"
                        : "Serviço"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {row.data.price !== undefined
                    ? `R$ ${row.data.price.toFixed(2).replace(".", ",")}`
                    : "-"}
                </TableCell>
                <TableCell>{row.data.sku || "-"}</TableCell>
                <TableCell className="text-right">
                  {!row.isValid && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 px-2 text-xs" 
                      onClick={() => handleEditClick(row.originalIndex)}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Corrigir
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              )
            ))}
          </TableBody>
        </Table>
        {filteredRows.length > 50 && (
          <p className="text-center text-sm text-muted-foreground py-2">
            Mostrando 50 de {filteredRows.length} itens {filterStatus !== 'all' ? (filterStatus === 'valid' ? 'válidos' : 'com erro') : ''}
          </p>
        )}
      </ScrollArea>
    </div>
  );
  };

  const renderImportingStep = () => (
    <div className="space-y-6 py-8 text-center">
      <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
      <div className="space-y-2">
        <p className="font-medium">Importando produtos...</p>
        <Progress value={importProgress} className="w-full max-w-sm mx-auto" />
        <p className="text-sm text-muted-foreground">
          {Math.round(importProgress)}% concluído
        </p>
      </div>
    </div>
  );

  const renderCompleteStep = () => {
    if (!importResult) return null;

    const { success, alreadyExists, csvDuplicates, errors } = importResult;
    const hasRealErrors = errors.length > 0;
    const sections: { title: string; items: SkippedItem[]; tone: "muted" | "warning" | "destructive" }[] = [];

    if (alreadyExists.length > 0) {
      sections.push({
        title: `${alreadyExists.length} já existia(m) no catálogo`,
        items: alreadyExists,
        tone: "muted",
      });
    }
    if (csvDuplicates.length > 0) {
      sections.push({
        title: `${csvDuplicates.length} duplicado(s) no próprio arquivo`,
        items: csvDuplicates,
        tone: "warning",
      });
    }
    if (errors.length > 0) {
      sections.push({
        title: `${errors.length} erro(s) na importação`,
        items: errors,
        tone: "destructive",
      });
    }

    return (
      <div className="space-y-6 py-4 text-center">
        {success > 0 && (
          <div className="space-y-2">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
            <p className="text-lg font-medium">Importação concluída!</p>
          </div>
        )}

        <div className="flex justify-center gap-2 flex-wrap mt-4">
          <Badge variant="default" className="text-sm py-1 px-3">
            {success} importado(s)
          </Badge>
          {alreadyExists.length > 0 && (
            <Badge variant="secondary" className="text-sm py-1 px-3">
              {alreadyExists.length} já existia(m)
            </Badge>
          )}
          {csvDuplicates.length > 0 && (
            <Badge variant="outline" className="text-sm py-1 px-3 border-yellow-500/50 text-yellow-700">
              {csvDuplicates.length} duplicado(s) no CSV
            </Badge>
          )}
          {hasRealErrors && (
            <Badge variant="destructive" className="text-sm py-1 px-3">
              {errors.length} erro(s)
            </Badge>
          )}
        </div>

        {sections.length > 0 && (
          <>
            <ScrollArea className="h-[260px] text-left mt-4">
              <div className="space-y-4">
                {sections.map((section) => (
                  <div key={section.title} className="space-y-2">
                    <p className="text-sm font-semibold">{section.title}</p>
                    <div className="space-y-1">
                      {section.items.map((item, idx) => {
                        const bg =
                          section.tone === "destructive"
                            ? "bg-destructive/10"
                            : section.tone === "warning"
                              ? "bg-yellow-500/10"
                              : "bg-muted";
                        const Icon = section.tone === "destructive" ? XCircle : AlertCircle;
                        const iconColor =
                          section.tone === "destructive"
                            ? "text-destructive"
                            : section.tone === "warning"
                              ? "text-yellow-600"
                              : "text-muted-foreground";
                        return (
                          <div
                            key={`${section.title}-${idx}`}
                            className={`flex items-start gap-2 p-2 rounded text-sm ${bg}`}
                          >
                            <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${iconColor}`} />
                            <div className="min-w-0">
                              <span className="font-medium">
                                {item.row > 0 ? `Linha ${item.row}: ` : ""}{item.name}
                              </span>
                              <p className="text-muted-foreground text-xs">
                                {item.message}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={handleRetryErrors} className="border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Pencil className="w-4 h-4 mr-2" />
                Tentar corrigir itens ignorados
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importar Produtos via CSV
          </DialogTitle>
          <DialogDescription>
            {step === "upload" && "Faça upload de um arquivo CSV ou Excel com seus produtos."}
            {step === "mapping" && "Configure o mapeamento das colunas do arquivo."}
            {step === "preview" && "Revise os dados antes de importar."}
            {step === "importing" && "Aguarde enquanto os produtos são importados..."}
            {step === "complete" && "Veja o resultado da importação."}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && renderUploadStep()}
        {step === "mapping" && renderMappingStep()}
        {step === "preview" && renderPreviewStep()}
        {step === "importing" && renderImportingStep()}
        {step === "complete" && renderCompleteStep()}

        <DialogFooter className="gap-2">
          {step === "mapping" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>
                Voltar
              </Button>
              <Button onClick={handleContinueToPreview}>
                Continuar
              </Button>
            </>
          )}
          {step === "preview" && (
            <div className="flex w-full items-center justify-between">
              <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                {errorCount > 0 && (
                  <>
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <span>Os {errorCount} itens com erro serão ignorados.</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setStep("mapping")}>
                  Voltar
                </Button>
                <Button onClick={handleStartImport} disabled={validCount === 0}>
                  Importar {validCount} item(s)
                </Button>
              </div>
            </div>
          )}
          {step === "complete" && (
            <Button onClick={() => handleClose(true)}>
              Concluir
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
